import React, { FC } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ShowChartIcon from '@mui/icons-material/ShowChart';

import { useGetResourceEventsQuery } from './healthWalletSlice';
import { BRAND, BRAND_DARK, BRAND_DARKER, formatDate } from './healthWalletConstants';
import type { GroupedSummary } from './healthWalletConstants';
import { EventTimeline } from './EventTimeline';

// --- Detail Dialog with Timeline (lazy-loads events from backend) ---
export const GroupedDetailDialog: FC<{
  group: GroupedSummary | null;
  resource: string;
  open: boolean;
  onClose: () => void;
}> = ({ group, resource, open, onClose }) => {
  const queryArg = { resource, name: group?.name ?? '' };
  const { data: events, isLoading: eventsLoading, isFetching } = useGetResourceEventsQuery(
    queryArg,
    { skip: !group || !open }
  );

  const handleClose = () => { onClose(); };

  if (!group) return null;

  const displayName = group.name || 'Other / Uncategorized';
  const loading = eventsLoading || isFetching;
  const eventList = loading ? [] : (events ?? []);
  // Chart derived client-side from THIS group's numeric events (>=2 points sharing a unit). get_patient_trends
  // computes the strict chartable set separately (the Trends tab); this just draws a line when a drilled group
  // happens to have plottable values. Preserve original eventList indices for chart<->timeline hover sync.
  const chartData = (() => {
    const numeric = eventList
      .map((e: any, idx: number) => ({ e, idx }))
      .filter(({ e }) => e?.value !== null && e?.value !== undefined && !isNaN(Number(e.value)));
    if (numeric.length < 2) return null;
    const unit = numeric[0].e.unit ?? '';
    const pts = numeric.filter(({ e }) => (e.unit ?? '') === unit);
    if (pts.length < 2) return null;
    return { unit, data: pts.map(({ e, idx }) => ({ date: e.start ?? '', value: Number(e.value), eventIndex: idx })) };
  })();

  return (
    // transitionDuration={0}: MUI's default ~225ms open / ~195ms close Fade removed — the events fetch
    // runs concurrently anyway, so the fade only delayed seeing the spinner/content. Instant = snappy.
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth transitionDuration={0} aria-labelledby="grouped-detail-dialog-title" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${BRAND_DARKER} 0%, ${BRAND_DARK} 100%)`,
        color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, px: 3,
      }}>
        <Box>
          <Typography variant="h6" fontWeight="bold" id="grouped-detail-dialog-title">{displayName}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>{group.count} event{group.count !== 1 ? 's' : ''}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="Close dialog" sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '24px !important', pb: 2, px: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: BRAND_DARKER }} /></Box>
        ) : (
          <EventTimeline
            key={`${resource}-${group.name}`}
            eventList={eventList}
            chartData={chartData}
            resource={resource}
            groupName={group.name}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// --- Grouped Resource Card Row ---
export const GroupedRow: FC<{ group: GroupedSummary; onClick: () => void }> = ({ group, onClick }) => (
  <Card
    variant="outlined"
    sx={{
      mb: 1.5, borderRadius: 2, border: '1px solid #efeaff',
      transition: 'all 0.15s ease', cursor: 'pointer',
      '&:hover': { borderColor: BRAND_DARK, bgcolor: '#f7f7fa', transform: 'translateY(-1px)', boxShadow: '0 2px 8px rgba(108,76,224,0.08)' },
    }}
  >
    <CardActionArea onClick={onClick} sx={{ px: 2.5, py: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {group.name || 'Other / Uncategorized'}
            </Typography>
            {group.hasChart && (
              <ShowChartIcon sx={{ fontSize: 16, color: BRAND_DARKER }} aria-label="Has trend chart" />
            )}
            {group.count > 1 && (
              <Chip size="small" label={`${group.count}`}
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: BRAND, color: BRAND_DARKER, minWidth: 28 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {group.resource && (
              <Chip size="small" label={group.resource}
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600, bgcolor: '#efeaff', color: BRAND_DARKER, textTransform: 'capitalize' }} />
            )}
            <Typography variant="caption" color="text.secondary">
              {group.count > 1
                ? `${formatDate(group.firstDate)} — ${formatDate(group.lastDate)}`
                : formatDate(group.firstDate)}
            </Typography>
          </Box>
        </Box>
        <ChevronRightIcon sx={{ color: BRAND_DARK, ml: 1 }} />
      </Box>
    </CardActionArea>
  </Card>
);
